import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Card,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TextField,
  InputAdornment,
  Typography,
  MenuItem,
  TablePagination,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Chip,
} from "@mui/material";
import {
  Search,
  Add,
  Refresh,
  Close,
  CreditCard,
  Download,
  Layers,
} from "@mui/icons-material";
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import api from "../../api/axiosInstance";
import invoiceService from "../../services/invoiceService";
import StatusChip from "../../components/common/StatusChip";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import KpiCard from "../../components/common/KpiCard";
import { selectStatusOptions } from "../../redux/slices/statusDefinitionsSlice";
import { selectUsdToCiRate } from "../../redux/slices/systemSettingsSlice";
import { formatCiWithUsd } from "../../utils/currency";

const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "bank_transfer", label: "EFT" },
  { value: "card", label: "Card" },
  { value: "other", label: "Other" },
];

// ── Record Payment Dialog ──────────────────────────────────────────────────────
const RecordPaymentDialog = ({ open, onClose, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    invoiceId: "",
    amount: "",
    method: "cash",
    transactionId: "",
    notes: "",
  });

  useEffect(() => {
    if (!open) return;
    // Load outstanding invoices
    invoiceService
      .getAll({ status: "sent", limit: 100 })
      .then(({ data }) => setInvoices(data.data || []))
      .catch(() => { });
    setForm({
      invoiceId: "",
      amount: "",
      method: "cash",
      transactionId: "",
      notes: "",
    });
  }, [open]);

  const selectedInvoice = invoices.find((i) => i._id === form.invoiceId);

  const set = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));

  // Auto-fill amount when invoice selected
  const handleInvoiceChange = (e) => {
    const inv = invoices.find((i) => i._id === e.target.value);
    setForm((p) => ({
      ...p,
      invoiceId: e.target.value,
      amount: inv ? inv.balanceDue.toFixed(2) : "",
    }));
  };

  const handleSave = async () => {
    if (!form.invoiceId || !form.amount) return;
    setSaving(true);
    try {
      await invoiceService.pay(form.invoiceId, {
        amount: parseFloat(form.amount),
        method: form.method,
        transactionId: form.transactionId || undefined,
        notes: form.notes || undefined,
      });
      enqueueSnackbar("Payment recorded successfully", { variant: "success" });
      onSaved();
      onClose();
    } catch (err) {
      enqueueSnackbar(
        err.response?.data?.message || "Failed to record payment",
        { variant: "error" },
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography fontWeight={700}>Record Manual Payment</Typography>
        <IconButton size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Use this form to record cash or bank transfer payments received
            outside the online payment system.
          </Alert>

          <TextField
            select
            fullWidth
            label="Invoice *"
            value={form.invoiceId}
            onChange={handleInvoiceChange}
            size="small"
          >
            <MenuItem value="">
              <em>Select invoice…</em>
            </MenuItem>
            {invoices.map((inv) => (
              <MenuItem key={inv._id} value={inv._id}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                    gap: 2,
                  }}
                >
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {inv.invoiceNumber}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    maxWidth={180}
                  >
                    {inv.customer?.firstName} {inv.customer?.lastName}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color="error.main"
                    sx={{ ml: "auto", flexShrink: 0 }}
                  >
                    CI${(inv.balanceDue || 0).toFixed(2)}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>

          {selectedInvoice && (
            <Box sx={{ bgcolor: "action.hover", borderRadius: 2, p: 1.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mb={0.5}
              >
                Invoice details
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="caption">
                  {selectedInvoice.shipment?.packageDescription ||
                    "Shipment charges"}
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="error.main"
                >
                  Balance due: CI${(selectedInvoice.balanceDue || 0).toFixed(2)}
                </Typography>
              </Box>
            </Box>
          )}

          <Divider />

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              label="Amount (USD) *"
              type="number"
              value={form.amount}
              onChange={set("amount")}
              size="small"
              inputProps={{ min: 0, step: 0.01 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <Typography
                      sx={{ mr: 0.5, color: "text.secondary", fontSize: 13 }}
                    >
                      $
                    </Typography>
                  ),
                },
              }}
            />
            <TextField
              select
              label="Payment Method"
              value={form.method}
              onChange={set("method")}
              size="small"
            >
              {PAYMENT_METHODS.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <TextField
            label="Transaction / Reference ID (optional)"
            value={form.transactionId}
            onChange={set("transactionId")}
            size="small"
            placeholder="e.g. bank transfer ref number"
          />

          <TextField
            label="Notes (optional)"
            multiline
            rows={2}
            value={form.notes}
            onChange={set("notes")}
            size="small"
            placeholder="e.g. Cash received at office by J. Smith"
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!form.invoiceId || !form.amount || saving}
          startIcon={saving ? null : <CreditCard />}
        >
          {saving ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            `Record $${parseFloat(form.amount || 0).toFixed(2)} Payment`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Invoice Payments Detail Dialog ──────────────────────────────────────────────
// Shows every Payment record on ONE invoice, oldest first — how "Paid in
// Full" (a single payment for the full balance) vs "Multiple Payments"
// (several lines, each its own method/transaction ID, from
// MarkInvoicePaidDialog.jsx) actually breaks down. Reuses payments already
// loaded on this page rather than a separate fetch.
const InvoicePaymentsDialog = ({ open, invoiceLabel, payments, onClose }) => {
  const total = payments.reduce((s, p) => s + (p.amount || 0), 0);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography fontWeight={700}>Payments on {invoiceLabel}</Typography>
          <Typography variant="caption" color="text.secondary">
            {payments.length} payment{payments.length !== 1 ? "s" : ""} · CI${total.toFixed(2)} total
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {payments.map((p, i) => (
          <Box
            key={p._id}
            sx={{
              px: 2.5,
              py: 1.75,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 2,
              borderBottom: i < payments.length - 1 ? "1px solid" : "none",
              borderColor: "divider",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} sx={{ textTransform: "capitalize" }}>
                Payment {i + 1} — {(p.method || "").replace("_", " ")}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {dayjs(p.processedAt || p.createdAt).format("MMM D, YYYY · h:mm A")}
              </Typography>
              {p.transactionId && (
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }} display="block">
                  Ref: {p.transactionId}
                </Typography>
              )}
              {p.notes && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                  {p.notes}
                </Typography>
              )}
            </Box>
            <Typography fontWeight={700} color="success.main" sx={{ flexShrink: 0, whiteSpace: "nowrap" }}>
              CI${(p.amount || 0).toFixed(2)}
            </Typography>
          </Box>
        ))}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main Page ──────────────────────────────────────────────────────────────────
const AdminPayments = () => {
  const { enqueueSnackbar } = useSnackbar();
  const statusOptions = useSelector(selectStatusOptions("payment"));
  const usdToCiRate = useSelector(selectUsdToCiRate);
  const STATUS_FILTERS = [{ value: "", label: "All Statuses" }, ...statusOptions];

  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [recordOpen, setRecordOpen] = useState(false);
  // Every payment ever fetched, grouped by invoice — used only to show
  // "Single" vs "Multiple Payments" per row and back the detail dialog, NOT
  // for the table itself (that stays the existing flat, filtered/paginated
  // `payments` list). Reflects each invoice's FULL payment history
  // regardless of the current search/status filter or page.
  const [paymentsByInvoice, setPaymentsByInvoice] = useState({});
  const [detailTarget, setDetailTarget] = useState(null); // { invoiceId, invoiceLabel } | null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [payRes, sumRes] = await Promise.all([
        api.get("/invoices/payments"),
        invoiceService.getSummary(),
      ]);
      const all = payRes.data.data?.payments || [];
      setSummary(sumRes.data.data || null);

      const byInvoice = {};
      all.forEach((p) => {
        const invoiceId = p.invoice?._id;
        if (!invoiceId) return;
        if (!byInvoice[invoiceId]) byInvoice[invoiceId] = [];
        byInvoice[invoiceId].push(p);
      });
      Object.values(byInvoice).forEach((list) =>
        list.sort((a, b) => new Date(a.processedAt || a.createdAt) - new Date(b.processedAt || b.createdAt)),
      );
      setPaymentsByInvoice(byInvoice);

      // Client-side filter by status and search since payments API doesn't paginate
      let filtered = all;
      if (statusFilter)
        filtered = filtered.filter((p) => p.status === statusFilter);
      if (search) {
        const q = search.toLowerCase();
        filtered = filtered.filter(
          (p) =>
            p.invoice?.invoiceNumber?.toLowerCase().includes(q) ||
            p.customer?.firstName?.toLowerCase().includes(q) ||
            p.customer?.lastName?.toLowerCase().includes(q) ||
            p.transactionId?.toLowerCase().includes(q),
        );
      }

      setTotal(filtered.length);
      const start = page * rowsPerPage;
      setPayments(filtered.slice(start, start + rowsPerPage));
    } catch {
      enqueueSnackbar("Failed to load payments", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, search, enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [page, rowsPerPage, statusFilter]);

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setPage(0);
      load();
    }
  };

  const collectionRate = summary
    ? Math.round(
      ((summary.totalCollected || 0) /
        ((summary.totalCollected || 0) + (summary.outstanding || 1))) *
      100,
    )
    : 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Payments"
        subtitle={`${total} payment records`}
        action={
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button variant="outlined" size="small" startIcon={<Download />}>
              Export
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={() => setRecordOpen(true)}
            >
              Record Payment
            </Button>
          </Box>
        }
      />

      {/* KPIs */}
      <Box
        sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2 }}
      >
        <KpiCard
          label="Total Collected"
          value={formatCiWithUsd(summary?.totalCollected || 0, usdToCiRate)}
          delta="Sum of completed payments"
          deltaType="up"
          icon={<CreditCard />}
          iconBg="#ECFDF5"
          iconColor="#0E9F6E"
        />
        <KpiCard
          label="Outstanding"
          value={formatCiWithUsd(summary?.outstanding || 0, usdToCiRate)}
          delta={`${summary?.outstandingCount || 0} invoice(s) unpaid`}
          deltaType={summary?.outstanding > 0 ? "down" : "neutral"}
          icon={<CreditCard />}
          iconBg="#FFF5F5"
          iconColor="#C53030"
        />
        <KpiCard
          label="Paid Invoices"
          value={(summary?.paidCount || 0).toString()}
          delta="Settled invoices"
          deltaType="up"
          icon={<CreditCard />}
          iconBg="#EBF4FF"
          iconColor="#2B6CB0"
        />
        <KpiCard
          label="Collection Rate"
          value={`${collectionRate}%`}
          delta={collectionRate >= 70 ? "On track" : "Needs attention"}
          deltaType={collectionRate >= 70 ? "up" : "down"}
          icon={<CreditCard />}
          iconBg="#E9D8FD"
          iconColor="#553C9A"
        />
      </Box>

      <Card>
        {/* Toolbar */}
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            gap: 1.5,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <TextField
            size="small"
            placeholder="Search invoice #, customer, transaction ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleSearch}
            sx={{ width: 300 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 16, color: "text.disabled" }} />
                  </InputAdornment>
                ),
              },
            }}
          />
          <TextField
            select
            size="small"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
            sx={{ width: 180 }}
          >
            {STATUS_FILTERS.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>
          <IconButton size="small" onClick={load}>
            <Refresh />
          </IconButton>
        </Box>

        {loading && <LinearProgress />}

        {!loading && payments.length === 0 ? (
          <EmptyState
            icon="💳"
            title="No payments found"
            description="Payments will appear here once invoices are settled."
            actionLabel="Record Payment"
            onAction={() => setRecordOpen(true)}
          />
        ) : (
          <>
            <Box sx={{ overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Transaction ID</TableCell>
                    <TableCell>Invoice #</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Payment Plan</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map((p) => (
                    <TableRow key={p._id} hover>
                      <TableCell
                        sx={{
                          fontFamily: "monospace",
                          fontSize: 11,
                          color: "text.secondary",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {p.transactionId || "—"}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "monospace",
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {p.invoice?.invoiceNumber || "—"}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {p.customer?.firstName} {p.customer?.lastName}
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 700,
                          color: "success.main",
                          whiteSpace: "nowrap",
                        }}
                      >
                        CI${(p.amount || 0).toFixed(2)}
                      </TableCell>
                      <TableCell
                        sx={{ textTransform: "capitalize", fontSize: 13 }}
                      >
                        {(p.method || "").replace("_", " ")}
                      </TableCell>
                      <TableCell>
                        <StatusChip status={p.status} domain="payment" />
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const invoiceId = p.invoice?._id;
                          const siblings = invoiceId ? paymentsByInvoice[invoiceId] || [p] : [p];
                          const isMultiple = siblings.length > 1;
                          return (
                            <Chip
                              size="small"
                              variant="outlined"
                              clickable={!!invoiceId}
                              onClick={
                                invoiceId
                                  ? () =>
                                    setDetailTarget({
                                      invoiceId,
                                      invoiceLabel: p.invoice?.invoiceNumber || "invoice",
                                    })
                                  : undefined
                              }
                              icon={
                                isMultiple ? (
                                  <Layers sx={{ fontSize: 14 }} />
                                ) : (
                                  <TaskAltIcon sx={{ fontSize: 14 }} />
                                )
                              }
                              label={isMultiple ? `Multiple (${siblings.length})` : "Single"}
                              color={isMultiple ? "primary" : "default"}
                              sx={{ fontSize: 11 }}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: 12,
                          color: "text.secondary",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {dayjs(p.processedAt || p.createdAt).format(
                          "MMM D, YYYY",
                        )}
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          display="block"
                        >
                          {dayjs(p.processedAt || p.createdAt).format("h:mm A")}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: 12,
                          color: "text.secondary",
                          maxWidth: 180,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                        >
                          {p.notes || "—"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(+e.target.value);
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50]}
              sx={{ borderTop: "1px solid", borderColor: "divider" }}
            />
          </>
        )}
      </Card>

      <RecordPaymentDialog
        open={recordOpen}
        onClose={() => setRecordOpen(false)}
        onSaved={load}
      />

      <InvoicePaymentsDialog
        open={!!detailTarget}
        invoiceLabel={detailTarget?.invoiceLabel}
        payments={detailTarget ? paymentsByInvoice[detailTarget.invoiceId] || [] : []}
        onClose={() => setDetailTarget(null)}
      />
    </Box>
  );
};

export default AdminPayments;
